<div class="mt-4 sm:mt-8">
  <div class="surface-card overflow-hidden p-0">

    <div class="bg-gradient-to-r from-primary to-primary-deep px-3 sm:px-6 lg:px-8 py-4 sm:py-5">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-4">
        <div class="flex items-start sm:items-center gap-3">
          <div
            class="w-9 h-9 sm:w-10 sm:h-10 bg-bg-base/20 border border-border-subtle/20 rounded-xl flex items-center justify-center shadow-inner shrink-0 mt-0.5 sm:mt-0">
            <i class="fa-solid fa-calendar-days text-xs sm:text-sm text-text-primary"></i>
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="text-base sm:text-xl font-bold text-text-primary break-words">{{ 'unitDetails.calendar.title' | translate }}</h3>
            <p
              class="text-[10px] sm:text-[11px] font-bold text-text-secondary mt-1 sm:mt-0.5 flex flex-wrap items-center gap-1.5 leading-tight">
              @if (selectedStartDate && selectedEndDate) {
              <span class="bg-bg-base/30 px-2 py-0.5 rounded-md font-mono text-text-primary whitespace-nowrap">
                {{ selectedStartDate | date: 'MMM d' }}
              </span>
              <span class="text-text-muted">→</span>
              <span class="bg-bg-base/30 px-2 py-0.5 rounded-md font-mono text-text-primary whitespace-nowrap">
                {{ selectedEndDate | date: 'MMM d, y' }}
              </span>
              } @else if (selectedStartDate) {
              <span class="inline-flex items-center gap-1"><span
                  class="w-1.5 h-1.5 rounded-full bg-warning animate-ping shrink-0"></span> Select target end
                date</span>
              } @else {
              <span>{{ 'unitDetails.calendar.subtitle' | translate }}</span>
              }
            </p>
          </div>
        </div>

        @if (selectedStartDate) {
        <button type="button" (click)="clearSelection()"
        class="inline-flex w-full sm:w-auto shrink-0 justify-center items-center gap-1.5 px-4 sm:px-3 py-2 sm:py-1.5 bg-bg-base/20 hover:bg-bg-base/40 text-text-primary text-[11px] sm:text-xs font-bold uppercase tracking-wider rounded-xl transition-all duration-200 active:scale-[0.97] cursor-pointer border border-border-subtle/20">
          <i class="fa-solid fa-xmark text-[10px]"></i> {{ 'unitDetails.calendar.clearSelection' | translate }}
        </button>
        }
      </div>
    </div>

    <div class="flex flex-col min-[420px]:flex-row min-[420px]:items-center min-[420px]:justify-between gap-3 px-3 sm:px-8 py-3 bg-bg-secondary border-b border-border-subtle">
      <div class="flex items-center gap-2">
        <span class="w-2 h-2 rounded-full bg-success animate-pulse"></span>
        <span class="text-[11px] font-bold uppercase tracking-wider text-text-muted">Live Diagnostics</span>
      </div>
      <div class="flex w-full min-[420px]:w-auto items-center justify-center gap-1.5 surface p-1 px-2.5 rounded-lg border border-border-subtle shadow-sm">
        <span class="text-xs font-black text-success font-mono">{{ availableCount | roundToTenth }}</span>
        <span class="text-[10px] font-bold text-text-muted uppercase tracking-tight">Days Vacant</span>
      </div>
    </div>

    <div class="flex items-center justify-between gap-3 px-3 sm:px-6 py-3 sm:py-4 bg-bg-base border-b border-border-subtle">
      <button type="button" (click)="previousMonth()"
        class="w-9 h-9 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-surface-hover border border-border-subtle rounded-xl transition-all duration-200 active:scale-95 cursor-pointer">
        <i class="fa-solid fa-chevron-left text-xs"></i>
      </button>

      <h4 class="caption text-center text-text-primary font-black uppercase tracking-widest font-mono min-w-0">
        {{ getMonthName() }}
      </h4>

      <button type="button" (click)="nextMonth()"
        class="w-9 h-9 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-surface-hover border border-border-subtle rounded-xl transition-all duration-200 active:scale-95 cursor-pointer">
        <i class="fa-solid fa-chevron-right text-xs"></i>
      </button>
    </div>

    <div class="p-3 sm:p-6 bg-bg-base">
      @if (isLoading) {
      <div class="flex flex-col justify-center items-center py-16 gap-3 animate-fadeIn">
        <div class="animate-spin rounded-full h-7 w-7 border-[3px] border-border-subtle border-b-primary"></div>
        <span class="text-[10px] font-bold uppercase tracking-wider text-text-muted">Syncing Matrix...</span>
      </div>
      }

      @if (errorMessage && !isLoading) {
      <div class="bg-danger/10 border border-danger/20 rounded-2xl p-5 text-center max-w-md mx-auto animate-fadeIn">
        <i class="fas fa-triangle-exclamation text-danger text-lg mb-2"></i>
        <p class="text-danger text-xs font-bold leading-normal">{{ errorMessage }}</p>
      </div>
      }

      @if (!isLoading && !errorMessage) {
      <div class="grid grid-cols-7 gap-0.5 min-[380px]:gap-1 sm:gap-2.5 mb-3 text-center">
        @for (dayName of ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']; track dayName) {
        <div class="text-[8px] min-[380px]:text-[9px] sm:text-[10px] font-black uppercase tracking-normal sm:tracking-wider text-text-muted py-1 font-mono truncate">
          {{ 'unitDetails.calendar.days.' + (dayName | lowercase).substring(0, 2) | translate }}
        </div>
        }
      </div>

      <div class="grid grid-cols-7 gap-0.5 min-[380px]:gap-1 sm:gap-2.5">
        @for (day of calendarDays; track day.fullDate) {
        <div [class]="getDayClasses(day)" (click)="onDateClick(day)"
          class="relative aspect-square min-h-9 flex flex-col items-center justify-center rounded-md min-[380px]:rounded-lg sm:rounded-2xl font-mono text-[10px] sm:text-xs font-bold transition-all duration-150 select-none cursor-pointer group p-0.5 sm:p-0">
          <span class="relative z-10 transition-transform group-hover:scale-105">
            {{ day.date | roundToTenth }}
          </span>
          @if (day.status !== 'past' && day.price) {
          <!-- <span
            class="text-[8px] sm:text-[9px] font-bold text-text-muted mt-0.5 leading-none transition-colors group-hover:text-text-primary"
            [class.!text-text-primary]="day.isSelected">
            {{ day.priceLabel }}
          </span> -->
          }

          @if (day.isToday && !day.isSelected) {
          <div class="absolute bottom-1 sm:bottom-1.5 w-1 h-1 bg-info rounded-full shadow-[0_0_4px_rgba(var(--color-info),0.5)]">
          </div>
          }
        </div>
        }
      </div>
      }
    </div>

    <div class="bg-bg-secondary px-3 sm:px-8 py-4 sm:py-5 border-t border-border-subtle">
      <div class="grid grid-cols-1 min-[380px]:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 caption font-bold text-text-secondary">
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="w-3.5 h-3.5 bg-success/20 border border-success/40 rounded-md shadow-sm"></div>
          <span>{{ 'unitDetails.calendar.available' | translate }}</span>
        </div>
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="w-3.5 h-3.5 bg-surface border border-border-subtle rounded-md"></div>
          <span>{{ 'unitDetails.calendar.unavailable' | translate }}</span>
        </div>
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="w-3.5 h-3.5 bg-bg-base border border-border rounded-md opacity-40"></div>
          <span>{{ 'unitDetails.calendar.past' | translate }}</span>
        </div>
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="w-3.5 h-3.5 bg-primary rounded-md shadow-sm"></div>
          <span class="text-text-primary">{{ 'unitDetails.calendar.selected' | translate }}</span>
        </div>
      </div>

      <div
        class="mt-4 pt-3 border-t border-border-subtle caption font-medium text-text-muted flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-2">
        <div class="flex items-start sm:items-center gap-2 min-w-0">
          <i class="fa-solid fa-circle-info text-primary text-xs shrink-0 mt-0.5 sm:mt-0"></i>
          <span class="leading-relaxed sm:leading-normal break-words">{{ 'unitDetails.calendar.operationalRuleStart' | translate }} <strong
              class="text-text-primary font-bold">{{ 'unitDetails.calendar.checkIn' | translate }}</strong> {{
            'unitDetails.calendar.operationalRuleMiddle' | translate }}
            <strong class="text-text-primary font-bold">{{ 'unitDetails.calendar.checkOut' | translate }}</strong> {{
            'unitDetails.calendar.operationalRuleEnd' | translate }}</span>
        </div>

        @if (selectedTotalPrice > 0) {
        <div class="flex flex-col items-start sm:items-end w-full sm:w-auto bg-surface sm:bg-transparent p-3 sm:p-0 rounded-lg sm:rounded-none border border-border-subtle sm:border-0 mt-2 sm:mt-0 min-w-0">
          <span class="caption text-text-muted uppercase tracking-wider font-bold">{{ 'unitDetails.calendar.totalPrice'
            | translate }} ({{ selectedTotalDays | roundToTenth }} {{ 'unitDetails.calendar.forNights' | translate |
            slice: 9 }})</span>
          <span class="text-primary font-black text-lg sm:h4 mt-0.5 sm:mt-0 break-words">{{ selectedTotalPriceLabel }} {{ currency }}</span>
        </div>
        }
      </div>
    </div>

  </div>
</div>
